import { useState, type FormEvent } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '../api/client.js';
import type { TeamUser, TenantDetail } from '../api/types.js';
import { useApp, useDocumentTitle, useMe } from '../app/context.js';
import { IconChevronLeft } from '../components/icons.js';
import { useToast } from '../components/toast.js';
import { RoleBadges, RoleEditor, Sheet, StatusBadge, dateDe } from '../components/ui.js';

/** Mandant im Detail: Status, Tools, Nutzer, Löschung mit Status der Tool-Antworten. */
export function AdminTenantPage() {
  const tenantId = useParams().tenantId!;
  const { api } = useApp();
  const me = useMe().data!;
  const qc = useQueryClient();
  const toast = useToast();
  const tenant = useQuery({ queryKey: ['admin', 'tenant', tenantId], queryFn: () => api.tenant(tenantId), enabled: me.platformAdmin });
  const catalog = useQuery({ queryKey: ['admin', 'catalog'], queryFn: () => api.catalog(), enabled: me.platformAdmin });
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<TeamUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  useDocumentTitle(tenant.data?.name ?? 'Mandant');

  const update = (t: TenantDetail) => {
    qc.setQueryData(['admin', 'tenant', tenantId], t);
    void qc.invalidateQueries({ queryKey: ['admin', 'tenants'] });
  };
  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') => api.updateTenant(tenantId, { status }),
    onSuccess: (t) => {
      update(t);
      toast(t.status === 'active' ? 'Mandant entsperrt.' : 'Mandant gesperrt.');
    },
  });
  const setApp = useMutation({
    mutationFn: ({ appId, enabled }: { appId: string; enabled: boolean }) => api.setApp(tenantId, appId, enabled),
    onSuccess: update,
  });

  if (!me.platformAdmin) return <Navigate to="/" replace />;
  if (tenant.isLoading || catalog.isLoading) return <div className="spinner">Lädt …</div>;
  if (tenant.error || !tenant.data) return <main className="page"><div className="alert alert--error">{(tenant.error as Error)?.message ?? 'Nicht gefunden'}</div></main>;

  const t = tenant.data;
  const apps = catalog.data?.apps ?? [];
  const assignable = apps.filter((a) => t.apps.includes(a.appId)).map(({ appId, name, roles }) => ({ appId, name, roles }));
  const locked = t.status === 'deleting' || t.status === 'deleted';
  const err = (setStatus.error ?? setApp.error) as ApiError | null;

  return (
    <main className="page">
      <header className="subheader">
        <Link to="/admin" className="icon-btn icon-btn--bare" aria-label="Zurück zu den Mandanten">
          <IconChevronLeft size={22} />
        </Link>
        <div className="page__head--stack">
          <h1 className="page__title">{t.name}</h1>
          <span className="muted">
            seit {dateDe(t.createdAt)}
            {t.contactEmail ? ` · ${t.contactEmail}` : ''} · <StatusBadge status={t.status} />
          </span>
        </div>
      </header>
      {err && <div className="alert alert--error">{err.message}</div>}

      <div className="columns">
        <section className="card">
          <h2 className="card__title">Tools</h2>
          {apps.map((a) => (
            <label key={a.appId} className="check">
              <input
                type="checkbox"
                checked={t.apps.includes(a.appId)}
                disabled={locked || setApp.isPending}
                onChange={(e) => setApp.mutate({ appId: a.appId, enabled: e.target.checked })}
              />
              <span>
                <strong>{a.name}</strong>
                <span className="muted">{a.description}</span>
              </span>
            </label>
          ))}
          <p className="note">Abschalten nimmt allen Nutzern den Zugriff; vergebene Rollen bleiben gespeichert.</p>
        </section>

        <section className="card">
          <div className="card__head">
            <h2 className="card__title">Nutzer</h2>
            {!locked && <button className="btn btn--ghost btn--sm" onClick={() => setInviting(true)}>+ Einladen</button>}
          </div>
          {t.users.map((u) => (
            <div key={u.userId} className="list__item list__item--flat">
              <div className="list__main">
                <strong>{u.name}</strong>
                <span className="muted">{u.email}</span>
                <RoleBadges roles={u.roles} apps={assignable} />
              </div>
              <div className="list__side">
                <StatusBadge status={u.status} />
                {!locked && <button className="btn btn--ghost btn--sm" onClick={() => setEditing(u)}>Bearbeiten</button>}
              </div>
            </div>
          ))}
          {t.users.length === 0 && <p className="note">Keine Nutzer.</p>}
        </section>
      </div>

      {t.deletions.length > 0 && (
        <section className="card">
          <h2 className="card__title">Löschung – Antworten der Tools</h2>
          {t.deletions.map((d) => (
            <div key={d.appId} className="list__item list__item--flat">
              <div className="list__main">
                <strong>{apps.find((a) => a.appId === d.appId)?.name ?? d.appId}</strong>
                <span className="muted">
                  angefragt {dateDe(d.requestedAt)}
                  {d.respondedAt ? ` · Antwort ${dateDe(d.respondedAt)}` : ''}
                  {d.retentionUntil ? ` · aufbewahrt bis ${dateDe(d.retentionUntil)}` : ''}
                </span>
              </div>
              <StatusBadge status={d.status} />
            </div>
          ))}
        </section>
      )}

      {!locked && (
        <section className="card card--danger">
          <h2 className="card__title">Mandant sperren oder löschen</h2>
          <div className="actions">
            {t.status === 'active' ? (
              <button className="btn btn--outline" disabled={setStatus.isPending} onClick={() => setStatus.mutate('suspended')}>Sperren</button>
            ) : (
              <button className="btn btn--outline" disabled={setStatus.isPending} onClick={() => setStatus.mutate('active')}>Entsperren</button>
            )}
            <button className="btn btn--danger" onClick={() => setDeleting(true)}>Löschen …</button>
          </div>
          <p className="note">Sperren: niemand im Betrieb kommt mehr in die Tools, Daten bleiben unverändert. Löschen: alle Konten werden entfernt, die Tools löschen oder sperren ihre Daten nach ihren Aufbewahrungsregeln.</p>
        </section>
      )}

      {inviting && <InviteSheet tenantId={tenantId} apps={assignable} onClose={() => setInviting(false)} onDone={update} />}
      {editing && <EditMemberSheet tenantId={tenantId} user={editing} apps={assignable} onClose={() => setEditing(null)} />}
      {deleting && <DeleteSheet tenant={t} onClose={() => setDeleting(false)} onDone={update} />}
    </main>
  );
}

function InviteSheet({ tenantId, apps, onClose, onDone }: { tenantId: string; apps: { appId: string; name: string; roles: { id: string; label: string }[] }[]; onClose: () => void; onDone: (t: TenantDetail) => void }) {
  const { api } = useApp();
  const toast = useToast();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>([]);
  const invite = useMutation({
    mutationFn: () => api.adminInvite(tenantId, { name: name.trim(), email: email.trim(), roles }),
    onSuccess: async (u) => {
      onDone(await api.tenant(tenantId));
      toast(`Einladung an ${u.email} verschickt.`);
      onClose();
    },
  });
  const valid = name.trim() && /^\S+@\S+\.\S+$/.test(email.trim());
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) invite.mutate();
  };
  return (
    <Sheet title="Person einladen" onClose={onClose}>
      <form onSubmit={submit} className="form">
        <div className="field">
          <label className="field__label" htmlFor="ai-name">Name</label>
          <input id="ai-name" className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="ai-email">E-Mail</label>
          <input id="ai-email" type="email" className="input" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <RoleEditor apps={apps} value={roles} onChange={setRoles} idPrefix="ai" withAdmin />
        {invite.error && <div className="alert alert--error">{(invite.error as ApiError).message}</div>}
        <div className="grid-2">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
          <button type="submit" className="btn btn--primary" disabled={!valid || invite.isPending}>Einladen</button>
        </div>
      </form>
    </Sheet>
  );
}

function EditMemberSheet({ tenantId, user, apps, onClose }: { tenantId: string; user: TeamUser; apps: { appId: string; name: string; roles: { id: string; label: string }[] }[]; onClose: () => void }) {
  const { api } = useApp();
  const qc = useQueryClient();
  const toast = useToast();
  const [roles, setRoles] = useState(user.roles);
  const [confirm, setConfirm] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ['admin', 'tenant', tenantId] });
  const save = useMutation({
    mutationFn: () => api.adminUpdateMember(tenantId, user.userId, { roles }),
    onSuccess: async () => {
      await refresh();
      toast('Rollen gespeichert.');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.adminRemoveMember(tenantId, user.userId),
    onSuccess: async () => {
      await refresh();
      toast(`${user.name} wurde entfernt.`);
      onClose();
    },
  });
  const err = (save.error ?? remove.error) as ApiError | null;
  return (
    <Sheet title={user.name} onClose={onClose}>
      <span className="muted">{user.email}</span>
      <RoleEditor apps={apps} value={roles} onChange={setRoles} idPrefix="am" withAdmin />
      {err && <div className="alert alert--error">{err.message}</div>}
      <div className="grid-2">
        <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
        <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>Speichern</button>
      </div>
      <div className="sheet__footer">
        {confirm ? (
          <div className="grid-2">
            <button type="button" className="btn btn--ghost" onClick={() => setConfirm(false)}>Nein</button>
            <button type="button" className="btn btn--danger" disabled={remove.isPending} onClick={() => remove.mutate()}>Ja, entfernen</button>
          </div>
        ) : (
          <button type="button" className="link-btn link-btn--danger" onClick={() => setConfirm(true)}>Nutzer entfernen</button>
        )}
      </div>
    </Sheet>
  );
}

function DeleteSheet({ tenant, onClose, onDone }: { tenant: TenantDetail; onClose: () => void; onDone: (t: TenantDetail) => void }) {
  const { api } = useApp();
  const toast = useToast();
  const [confirmName, setConfirmName] = useState('');
  const del = useMutation({
    mutationFn: () => api.deleteTenant(tenant.tenantId, confirmName),
    onSuccess: (t) => {
      onDone(t);
      toast('Löschung angestoßen – die Tools melden ihren Status.');
      onClose();
    },
  });
  return (
    <Sheet title="Mandant löschen" onClose={onClose}>
      <div className="alert alert--warn" style={{ flexDirection: 'column' }}>
        <strong>Das kann nicht rückgängig gemacht werden.</strong>
        <span>
          Alle {tenant.users.length} Konten werden gelöscht. Die Tools ({tenant.apps.join(', ') || 'keine'}) werden informiert und löschen oder sperren
          ihre Daten nach gesetzlichen Aufbewahrungsfristen. Biete dem Betrieb vorher an, seine Daten zu exportieren.
        </span>
      </div>
      <div className="field">
        <label className="field__label" htmlFor="del-confirm">Zur Bestätigung den Namen eingeben: {tenant.name}</label>
        <input id="del-confirm" className="input" value={confirmName} onChange={(e) => setConfirmName(e.target.value)} autoComplete="off" />
      </div>
      {del.error && <div className="alert alert--error">{(del.error as ApiError).message}</div>}
      <div className="grid-2">
        <button type="button" className="btn btn--ghost" onClick={onClose}>Abbrechen</button>
        <button type="button" className="btn btn--danger" disabled={confirmName !== tenant.name || del.isPending} onClick={() => del.mutate()}>
          Endgültig löschen
        </button>
      </div>
    </Sheet>
  );
}
